import React, { useEffect, useMemo, useState } from "react";
import api from "../../../api/axios";

interface SaleRow {
  id: number; dealId: number; number: string; date: string; name: string; sku: string | null;
  quantity: number; price: string; total: string; paymentMethod: string | null;
  client: string | null; manager: string | null; serials: string;
}

const iso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const money = (v: number) => v.toLocaleString("ru-RU", { maximumFractionDigits: 0 });

export default function SalesTab() {
  const today = new Date();
  const [from, setFrom] = useState(iso(new Date(today.getFullYear(), today.getMonth(), 1)));
  const [to, setTo] = useState(iso(today));
  const [q, setQ] = useState("");
  const [qDeb, setQDeb] = useState("");
  const [rows, setRows] = useState<SaleRow[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => { const t = setTimeout(() => setQDeb(q), 350); return () => clearTimeout(t); }, [q]);

  useEffect(() => {
    let off = false;
    setLoading(true);
    api.get("/warehouse/sales", { params: { from, to, q: qDeb } })
      .then((r) => { if (!off) setRows(r.data ?? []); })
      .catch(() => { if (!off) setRows([]); })
      .finally(() => { if (!off) setLoading(false); });
    return () => { off = true; };
  }, [from, to, qDeb]);

  const totals = useMemo(() => ({
    qty: rows.reduce((s, r) => s + Number(r.quantity), 0),
    sum: rows.reduce((s, r) => s + Number(r.total), 0),
    deals: new Set(rows.map((r) => r.dealId)).size,
  }), [rows]);

  const quick = (days: number) => { const d = new Date(); d.setDate(d.getDate() - days); setFrom(iso(d)); setTo(iso(new Date())); };

  return (
    <div className="warehouse-issues-tab">
      <div className="issues-header">
        <div className="issues-header-info">
          <h2 className="issues-title">Журнал продаж</h2>
          <div className="issues-stats">
            <span>{totals.deals} сделок</span><span className="issues-stats-dot">·</span>
            <span>{money(totals.qty)} шт.</span><span className="issues-stats-dot">·</span>
            <span>{money(totals.sum)} ₸</span>
          </div>
        </div>
      </div>

      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", padding: "0 20px 12px", alignItems: "center" }}>
        <input type="date" className="primary-input" value={from} onChange={(e) => setFrom(e.target.value)} />
        <span style={{ color: "#6b7280" }}>—</span>
        <input type="date" className="primary-input" value={to} onChange={(e) => setTo(e.target.value)} />
        <button className="secondary-btn" onClick={() => quick(0)}>Сегодня</button>
        <button className="secondary-btn" onClick={() => quick(7)}>7 дней</button>
        <button className="secondary-btn" onClick={() => quick(30)}>30 дней</button>
        <input className="primary-input" style={{ flex: "1 1 240px" }} placeholder="Поиск: товар, ШК, S/N, клиент, номер сделки" value={q} onChange={(e) => setQ(e.target.value)} />
      </div>

      <div style={{ padding: "0 20px 20px", overflowX: "auto" }}>
        <table className="stock-table">
          <thead>
            <tr><th>Дата</th><th>Сделка</th><th>Товар</th><th>Кол-во</th><th>Цена, ₸</th><th>Сумма, ₸</th><th>S/N</th><th>Клиент</th><th>Менеджер</th></tr>
          </thead>
          <tbody>
            {loading && rows.length === 0 && <tr><td colSpan={9} style={{ textAlign: "center", padding: 28, color: "#6b7280" }}>Загрузка…</td></tr>}
            {!loading && rows.length === 0 && <tr><td colSpan={9} style={{ textAlign: "center", padding: 28, color: "#6b7280" }}>Продаж за период нет</td></tr>}
            {rows.map((r) => (
              <tr key={r.id}>
                <td style={{ whiteSpace: "nowrap" }}>{new Date(r.date).toLocaleDateString("ru-RU")}</td>
                <td style={{ whiteSpace: "nowrap", color: "#818cf8" }}>{r.number}</td>
                <td>{r.name}{r.sku && <small style={{ display: "block", color: "#6b7280" }}>{r.sku}</small>}</td>
                <td>{r.quantity}</td>
                <td>{money(Number(r.price))}</td>
                <td>{money(Number(r.total))}</td>
                <td style={{ fontSize: 12, wordBreak: "break-all" }}>{r.serials || <span style={{ color: "#4b5563" }}>—</span>}</td>
                <td style={{ fontSize: 12 }}>{r.client || "—"}</td>
                <td style={{ fontSize: 12, color: "#9ca3af" }}>{r.manager || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {rows.length >= 3000 && <div style={{ color: "#fbbf24", fontSize: 12, marginTop: 8 }}>Показаны первые 3000 строк: сузьте период.</div>}
      </div>
    </div>
  );
}
